<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <!-- Form -->
                <div class="row">
                    <!-- Image -->
                    <div role="group" class="form-group col-12 col-md-4">
                        <label class=""> {{ $t('image') }}</label>
                        <GalleryPicker class="gallery-picker" v-model="coachingSession.photo" :ratioWidth="'1'" :ratioHeight="'1'" v-bind:class="{'is-invalid': genericValidator(coachingSession.photo, attemptedSubmit) === false || errorValidator('Photo') === false }" :max="1"></GalleryPicker>
                        <div v-if="errorValidator('Photo') === false" class="invalid-feedback d-block">{{ errorOutput['Photo'] }}</div>
                    </div>
                    <!-- backgroundImage -->
                    <div role="group" class="form-group col-12 col-md-8">
                        <label class=""> {{ $t('backgroundImage') }}</label>
                        <GalleryPicker class="gallery-picker" v-model="coachingSession.backgroundImage" v-bind:class="{'is-invalid': errorValidator('BackgroundImage') === false }" :max="1"></GalleryPicker>
                        <div v-if="errorValidator('BackgroundImage') === false" class="invalid-feedback d-block">{{ errorOutput['BackgroundImage'] }}</div>
                    </div>
                </div>
                <!-- Title -->
                <div role="group" class="form-group">
                    <label>{{ $t('titleMandatory') }}</label>
                    <CInput type="text" v-model="coachingSession.title" :invalid-feedback="errorOutput['Title']" :is-valid="genericValidator(coachingSession.title, attemptedSubmit) && errorValidator('Title')"></CInput>
                </div>
                <!-- Date -->
                <div role="group" class="form-group">
                    <label>{{ $t('dateMandatory') }}</label>
                    <CInput type="datetime-local" v-model="coachingSession.date" :invalid-feedback="errorOutput['Date']" :is-valid="genericValidator(coachingSession.date, attemptedSubmit) && errorValidator('Date')"></CInput>
                </div>
                <!-- Duration -->
                <div role="group" class="form-group">
                    <label class=""> {{ $t('durationMandatory') }}</label>
                    <CInput type="text" :placeholder="$t('minutes')" v-model="coachingSession.duration" :invalid-feedback="errorOutput['Duration']" :is-valid="genericValidator(coachingSession.title, attemptedSubmit) && errorValidator('Duration')"></CInput>
                </div>
                <!-- Invites -->
                <div role="group" class="form-group">
                    <label>{{ $t('students') }}</label>
                    <vue-tags-input 
                        v-model="invite" 
                        :tags="coachingSession.students" 
                        @tags-changed="updateStudents" 
                        v-bind:class="{'is-invalid': errorValidator('Students') || studentsApiError || coachingSession.students.length >= maxStudents }" 
                        class="flex-grow-1" 
                        :placeholder="$t('addStudents')"
                    />
                    <div v-if="studentsApiError" class="invalid-feedback d-block">{{ studentsApiError }}</div>
                    <div v-else-if="genericValidator(coachingSession.students, attemptedSubmit) === false" class="invalid-feedback d-block">{{errorOutput['Students']}}</div>
                    <span v-else-if="coachingSession.students.length >= maxStudents" class="invalid-feedback">{{ $t("maxStudentWarning") }}</span>
                </div>
                <!-- Coaching Session Spaces -->
                <div role="group" class="form-group" :class="{'disabled': $route.name !== 'CoachingSessionAdd' && this.baseCoachingSession && this.baseCoachingSession.coachingSessionSpaces}">
                    <label class=""> {{ $t('coachingSessionSpacesMandatory') }}</label>
                    <SingleOptionDropdown v-model="coachingSession.coachingSessionSpaces" :options="coachingSessionSpacesOptions" :title="$t('coachingSessionSpaces')" :index="1" :is-valid="genericValidator(coachingSession.coachingSessionSpaces, attemptedSubmit) && errorValidator('CoachingSessionSpaces')" :invalid-feedback="errorOutput['CoachingSessionSpaces']"></SingleOptionDropdown>
                </div>
                <!-- Boards -->
                <div role="group" class="form-group">
                    <label class=""> {{ $t('boardsMandatory') }}</label>
                    <OptionsDropdown v-model="coachingSession.boards" :options="boards" :title="$t('boards')" :index="1" :is-valid="genericValidator(coachingSession.boards, attemptedSubmit) && errorValidator('Boards')" :invalid-feedback="errorOutput['Boards']"></OptionsDropdown>
                </div>
                <!-- Groups -->
                <div role="group" class="form-group">
                    <label class=""> {{ $t('groupsMandatory') }}</label>
                    <OptionsDropdown v-model="coachingSession.groups" :options="groups" :title="$t('groups')" :index="2" :is-valid="genericValidator(coachingSession.groups, attemptedSubmit) && errorValidator('Groups')" :invalid-feedback="errorOutput['Groups']"></OptionsDropdown>
                </div>
                <!-- Description -->
                <div role="group" class="form-group">
                    <label>{{ $t('descriptionMandatory') }}</label>
                    <vue-editor v-model="coachingSession.description" v-bind:class="{'is-invalid': genericValidator(coachingSession.description, attemptedSubmit) == false || errorValidator(coachingSession.description) }"></vue-editor>
                    <div v-if="errorOutput['Description']" class="invalid-feedback">{{errorOutput['Description']}}</div>
                </div>
                <!-- Toggle Switch for Chat -->
                <div role="group" class="form-group d-flex align-items-center">
                    <label class="m-0">{{ $t('chat') }}</label>
                    <div class="d-flex align-items-center" :class="{'disabled': $route.name !== 'CoachingSessionAdd'}">
                        <CSwitch
                            v-model="chatEnabled"
                            :checked="chatEnabled"
                            @update:checked="toggleChat"
                            class="mx-2"
                            color="success"
                        />

                    </div>
                </div>
                <!-- Additional options if chat is enabled -->
                <div v-if="chatEnabled">
                    <!-- Select option for beforeClass or AfterClass -->
                    <div role="group" class="form-group" :class="{'disabled': $route.name !== 'CoachingSessionAdd'}">
                        <CSelect
                            v-model="coachingSession.messageType"
                            @input="onSelectChange"
                            :options="[ { value: 'BeforeClass', label: $t('beforeClass') }, { value: 'AfterClass', label: $t('afterClass') } ]" 
                        />
                    </div>
                    <!-- Message -->
                    <div v-if="coachingSession.messageType === 'BeforeClass'" class="form-group" :class="{'disabled': $route.name !== 'CoachingSessionAdd'}">
                        <label>{{ $t('firstChatMessageMandatory') }}</label>
                        <vue-editor v-model="coachingSession.coachFirstMessage" v-bind:class="{'disabled': baseMessages && baseMessages.length && baseMessages[0].text, 'is-invalid': genericValidator(coachingSession.coachFirstMessage, attemptedSubmit) == false || errorValidator(coachingSession.coachFirstMessage) }"></vue-editor>
                        <div v-if="errorOutput['CoachFirstMessage']" class="invalid-feedback">{{errorOutput['CoachFirstMessage']}}</div>
                    </div>
                </div>
                <!-- API errors that belong to no field above -->
                <FormApiErrors :fields="['BackgroundImage', 'Boards', 'CoachFirstMessage', 'CoachingSessionSpaces', 'Date', 'Description', 'Duration', 'Groups', 'Photo', 'Students', 'Title']"></FormApiErrors>
                <!-- Action button + submitting -->  
                <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary col-2 align-self-end" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import FormApiErrors from '@/controls/FormApiErrors.vue';
import { mapState, mapActions } from 'vuex'
import router from '@/router/index';
import { deepClone, sanitizeHtml } from "@/helpers/utils.js";
import { CONSTANTS } from '@/helpers/consts';
import { genericValidator, errorValidator } from "@/helpers/utils.js";
import CoachingSession from "@/classes/CoachingSession";
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import OptionsDropdown from '@/controls/OptionsDropdown.vue';
import SingleOptionDropdown from '@/controls/SingleOptionDropdown.vue';
import { VueEditor } from "vue2-editor";
import FormLoading from '@/controls/FormLoading.vue'
import VueTagsInput from '@johmun/vue-tags-input';
import InputGetList from "@/classes/InputGetList";
import { API_HELPER } from "@/helpers/api.js";
import GalleryPicker from '@/controls/GalleryPicker.vue'

  export default {
        components: { FormApiErrors, ConfirmationPopUp, VueEditor, OptionsDropdown, FormLoading, VueTagsInput, GalleryPicker, SingleOptionDropdown },
    props:{    
        baseCoachingSession:
        {
            type: Object,
            default: null,
        },
        baseMessages:
        {
            type: Array,
            default: null,
        },
        baseName:
        {
            type: String,
            default: null,
        }
    },
    data()
    {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            coachingSession: new CoachingSession([], [], []),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            invite:"",
            loading: false,
            chatEnabled: false,
            coachingSessionSpacesOptions: [
                { key: 'CoachStudents', label: this.$t('coachStudents') },
                { key: 'Students', label: this.$t('studentsDrodpwn') },
                { key: 'Single', label: this.$t('single') }
            ]
        }
    },
    mounted: function()
    {
        this.loadUsers();
        this.loadBoards();
        this.loadGroups();
        if(!this.baseCoachingSession) {
            this.coachingSession.messageType = 'None';
        }
    },
    computed:
    {
        ...mapState('users', ['getUsersOutput']),
        ...mapState('groups', ['getGroupsOutput']),
        ...mapState('boards', ['getBoardsOutput']),
        ...mapState('coachingSessions', ['putCoachingSessionOutput']),
        ...mapState('coachingSessions', ['postCoachingSessionOutput']),
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        // the API's field errors arrive keyed by name (e.g. Students: "The coach can have at most 5 students per session.")
        studentsApiError: function()
        {
            return this.alertType == 'alert-danger' && this.alertOutput && typeof this.alertOutput === 'object' ? this.alertOutput['Students'] : null;
        },
        errorOutput: function()
        {
            var result = [];

            if(this.alertType == 'alert-danger' && this.alertOutput && Array.isArray(this.alertOutput))
            {
                result = this.alertOutput.reduce(function(accum, currentVal) {
                accum[currentVal.key] = currentVal.value;
                    return accum;
                }, {});
            }

            // Validate required fields
            if(!this.coachingSession.title) { result['Title'] = this.$i18n.t("validateRequiredField"); }
            if(!this.coachingSession.date) { result['Date'] = this.$i18n.t("validateRequiredField"); }
            // if(!this.coachingSession.students || !this.coachingSession.students.length) { result['Students'] = this.$i18n.t("validateRequiredField"); }
            if(!this.coachingSession.coachingSessionSpaces) { result['CoachingSessionSpaces'] = this.$i18n.t("validateRequiredField"); }
            if(!this.coachingSession.boards || !this.coachingSession.boards.length) { result['Boards'] = this.$i18n.t("validateRequiredField"); }
            if(!this.coachingSession.groups || !this.coachingSession.groups.length) { result['Groups'] = this.$i18n.t("validateRequiredField"); }
            if(!this.coachingSession.photo) { result['Photo'] = this.$i18n.t("validateRequiredField"); }
            if(!this.coachingSession.backgroundImage) { result['BackgroundImage'] = this.$i18n.t("validateRequiredField"); }
            if(!this.coachingSession.duration) { result['Duration'] = this.$i18n.t("validateRequiredField"); }
            if(!this.coachingSession.description) { result['Description'] = this.$i18n.t("validateRequiredField"); }
            if(!this.coachingSession.coachFirstMessage) { result['CoachFirstMessage'] = this.$i18n.t("validateRequiredField"); }

            return result;
        },
        groupsInfo: function() {
            return this.getGroupsOutput ? this.getGroupsOutput : null;
        },
        groups: function()
        {
            return (this.groupsInfo?.data || []).map(x => ({ key: x.id.toString(), label: x.name }));
        },
        boardsInfo: function() {
            return this.getBoardsOutput ? this.getBoardsOutput : null;
        },
        boards: function()
        {
            return (this.boardsInfo?.data || []).map(x => ({ key: x.id.toString(), label: x.name }));
        },
        usersInfo: function() {
            return this.getUsersOutput ? this.getUsersOutput : null;
        },
        users: function()
        {
            return this.usersInfo && this.usersInfo.data ? this.usersInfo.data : [];
        },
        inviteEmails: function()
        {
            let emails = this.coachingSession.students
            .filter(x => /<(.*)>/.test(x))  // Only keep values that contain an email
            .map(x => x.match(/<(.*)>/)[1]);
            return emails;
        },
        filteredUsers: function() {
            return this.users.filter(i => {
                return i.email?.toLowerCase().indexOf(this.invite?.toLowerCase()) !== -1 &&
                        !this.inviteEmails.includes(i.email?.toLowerCase());
                }).map(i => {
                return i.firstName && i.lastName && i.email ? {text: `${i.firstName} ${i.lastName} <${i.email}>`} : {text: i.email};
            });
        },
        maxStudents: function()
        {
            var user = JSON.parse(localStorage.getItem('user'));
            return user && user.data && user.data.maxStudents ? user.data.maxStudents : 0;
        }
    },
    methods:{
        genericValidator,
        errorValidator,
        ...mapActions('boards', ['getBoards']),
        loadBoards: function()
        {
            this.loading = true;
            this.getBoards(new InputGetList(999, 1, '', 1, 'Name'));
        },
        ...mapActions('groups', ['getGroups']),
        loadGroups: function()
        {
            this.loading = true;
            this.getGroups(new InputGetList(999, 1, '', 1, 'Name'));
        },
        ...mapActions('users', ['getUsers']),
        loadUsers: function()
        {
            this.loading = true;
            var input = new InputGetList(999, 1, '', 1, 'FirstName');
            input.coach = false;
            this.getUsers(input);
        },
        resetCoachingSession(){
            if(!this.baseCoachingSession)
            {
                this.coachingSession = new CoachingSession([], [], []);
            }
            else
            {
                this.coachingSession = deepClone(new CoachingSession, this.baseCoachingSession);    
                this.coachingSession.groups = this.coachingSession.groups.map(group => group.id.toString());
                this.coachingSession.boards = this.coachingSession.boards.map(board => board.id.toString());
                this.coachingSession.students = this.coachingSession.students.map(x =>  ({text: x.email}));
                this.coachingSession.photo = this.coachingSession.photoPath ? {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.coachingSession.photoPath])} : null;       
                this.coachingSession.backgroundImage = this.coachingSession.backgroundImagePath ? {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.coachingSession.backgroundImagePath])} : null;       
                if(this.baseMessages && this.baseMessages.length) {
                    this.coachingSession.coachFirstMessage = this.baseMessages[0]?.text;
                }
                // the editor writes its value to innerHTML: never hand it unsanitized user HTML
                this.coachingSession.description = sanitizeHtml(this.coachingSession.description);
                this.coachingSession.coachFirstMessage = sanitizeHtml(this.coachingSession.coachFirstMessage);

                if(this.coachingSession.messageType === 'BeforeClass' || this.coachingSession.messageType === 'AfterClass') {
                    this.chatEnabled = true;
                }
            }
        },
        payloadImage(image) {
            if (!image) return null;

            if (!image.mime && !image.data && image.url) {
                return { data: "0", mime: "image/jpeg" };
            } else if (image.mime && image.data) {
                return image;
            } else if (image === null) {
                return null;
            }

        return image; // Default case to return the unchanged image object
        },
        submitClicked(){
            this.attemptedSubmit = true;

            // Emit event
            if(this.coachingSession.isValid())
            {
                this.submitting = true;
                // Extract emails from the students
                const extractedEmails = this.coachingSession.students
                    .map(student => {
                        const match = student.text.match(/<([^>]+)>/);
                        const email = match ? match[1] : student.text; // Use the match if available, otherwise use the original text
                        return email.trim(); // Trim the spaces from the email
                    });

                if (this.coachingSession) {
                    this.coachingSession.backgroundImage = this.payloadImage(this.coachingSession.backgroundImage);
                    this.coachingSession.photo = this.payloadImage(this.coachingSession.photo);
                }
                
                this.$emit('submit-requested', 
                { ...this.coachingSession, 
                    students: extractedEmails
                });
            }
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            router.push({name: "CoachingSessions" });
        },
        onProcessTask: function(val)
        {
            if(val != null)
            {
                if(val)
                {
                    this.showConfirmationPopup = true;
                }

                this.submitting = false;
            }
        },
        toggleChat() {
            this.chatEnabled = !this.chatEnabled;
        },
        onSelectChange(value) {
            this.coachingSession.messageType = value.target.value;
        },
        updateStudents(newInvites) {
            this.coachingSession.students = newInvites.slice(0, this.maxStudents).map( x =>  ({text: x.text}) );
        },
    },
    watch:
    {
        putCoachingSessionOutput: function(val)
        {
            this.onProcessTask(val);
        },
        postCoachingSessionOutput: function(val)
        {
            this.onProcessTask(val);
        },
        baseCoachingSession: function()
        {
            this.resetCoachingSession();
        },
        baseMessages: function(val)
        {
            // the messages can arrive after the session: fill the first message then (resetCoachingSession covers the other order)
            if(this.baseCoachingSession && val && val.length)
            {
                this.coachingSession.coachFirstMessage = sanitizeHtml(val[0]?.text);
            }
        },
        alertOutput: function()
        {
            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        },
        getGroupsOutput: function(val)
        {
            if(val != null && this.getBoardsOutput != null && this.getUsersOutput != null)
            {
                this.loading = false;
            }
        },
        getBoardsOutput: function(val)
        {
            if(val != null && this.getGroupsOutput != null && this.getUsersOutput != null)
            {
                this.loading = false;
            }
        },
        getUsersOutput: function(val)
        {
            if(val != null && this.getBoardsOutput != null && this.getGroupsOutput != null)
            {
                this.loading = false;
            }
        },
        chatEnabled: function(val) {
            if(val) {
                if(this.coachingSession.messageType === 'None') {
                    this.coachingSession.messageType = 'BeforeClass'; 
                }
            } else {
                this.coachingSession.messageType = 'None';
            }
        }
    }
  }
</script> 
 
<style>

@media only screen and (max-width: 767px) {
    .main-content {
        flex-direction: column;
    }
}

:deep(.quillWrapper.is-invalid .ql-snow.ql-toolbar),
:deep(.quillWrapper.is-invalid .ql-snow.ql-container),
:deep(.vue-tags-input.is-invalid .ti-input)
{
    border-color: #e55353;
}

:deep(.vue-tags-input){
    max-width: unset;
}
.dropdown-menu{
  max-height: 250px;
  overflow: auto;
}
</style> 